/**
 * Frame primitives — the structural reads every consumer of a chat stream
 * needs, and the entity reference that survives the map.
 *
 * Persisted `display_json` is raw JSON, not the typed union: sessions
 * recorded before the redesign carry retired frame types (`canvas.*`,
 * `knowledge.proposal`), and the contract says clients skip what they do not
 * recognize rather than crashing on it (DECISIONS #62). Everything that
 * folds frames reads them through these two accessors, so "unknown" is a
 * null, never an exception.
 */

import type { EntityKind, Ulid } from '../api/types';

/** The `type` of a raw/typed frame, or null when it is not a frame at all. */
export function frameType(frame: unknown): string | null {
  if (typeof frame !== 'object' || frame === null) return null;
  const t = (frame as { type?: unknown }).type;
  return typeof t === 'string' ? t : null;
}

/** The `data` object of a raw/typed frame, or null. */
export function frameData(frame: unknown): Record<string, unknown> | null {
  if (typeof frame !== 'object' || frame === null) return null;
  const d = (frame as { data?: unknown }).data;
  return typeof d === 'object' && d !== null ? (d as Record<string, unknown>) : null;
}

/**
 * A cross-artifact entity reference: explicit kind + opaque id + owning repo.
 * Prose chips carry one so a named component in an answer is recognizably
 * the real thing from the map, not a phrase the model liked.
 */
export interface EntityRef {
  kind: EntityKind;
  id: string;
  repo?: Ulid;
}
